/* Watch 360 - drag-to-rotate product spin from a frame sequence.
   <watch-360 data-base="https://.../spin/v1/" data-count="72" data-ext="webp" data-autoplay="true">
   Frames are <base>001.<ext> .. <base>NNN.<ext>. Progressive: the first frame is a normal <img>;
   the rest load once the viewer is near the viewport. Drag (mouse/touch), arrow keys, and a
   slow auto-spin until the visitor interacts (off with prefers-reduced-motion). */
(function () {
  'use strict';

  if (customElements.get('watch-360')) return;

  var MAX_INFLIGHT = 6;
  var AUTO_FPS = 12;

  function pad(n) { return String(n).padStart(3, '0'); }

  class Watch360 extends HTMLElement {
    connectedCallback() {
      this.base = this.dataset.base || '';
      this.count = parseInt(this.dataset.count, 10) || 0;
      this.ext = this.dataset.ext || 'webp';
      this.img = this.querySelector('.w360__img');
      if (!this.base || this.count < 2 || !this.img) return;

      this.frames = new Array(this.count);
      this.loaded = 0;
      this.index = 0;
      this.pos = 0; // fractional frame position while dragging
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay !== 'false' && !this.reduce;
      this.interacted = false;
      this.cleanup = [];

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            this.visible = e.isIntersecting;
            if (e.isIntersecting) this.preload();
            this.tick();
          }, this);
        }.bind(this),
        { rootMargin: '300px 0px' }
      );
      io.observe(this);
      this.cleanup.push(function () { io.disconnect(); });

      this.bindDrag();
      this.bindKeys();
    }

    disconnectedCallback() {
      (this.cleanup || []).forEach(function (fn) { fn(); });
      cancelAnimationFrame(this.raf);
    }

    url(i) { return this.base + pad(i + 1) + '.' + this.ext; }

    preload() {
      if (this.preloading) return;
      this.preloading = true;
      var next = 0;
      var inflight = 0;
      var self = this;
      // Load evenly spread frames first (every 8th, 4th, 2nd, ...) so the spin works early.
      var order = [];
      var seen = {};
      [8, 4, 2, 1].forEach(function (step) {
        for (var i = 0; i < self.count; i += step) {
          if (!seen[i]) { seen[i] = 1; order.push(i); }
        }
      });
      function pump() {
        while (inflight < MAX_INFLIGHT && next < order.length) {
          (function (i) {
            var im = new Image();
            im.decoding = 'async';
            inflight++;
            im.onload = function () {
              self.frames[i] = im;
              self.loaded++;
              inflight--;
              if (self.loaded === 1) self.classList.add('is-ready');
              pump();
            };
            im.onerror = function () { inflight--; pump(); };
            im.src = self.url(i);
          })(order[next++]);
        }
      }
      pump();
    }

    // Nearest loaded frame, so a half-loaded spin still turns smoothly.
    show(i) {
      i = ((i % this.count) + this.count) % this.count;
      var f = this.frames[i];
      if (!f) {
        for (var d = 1; d < this.count && !f; d++) {
          f = this.frames[(i + d) % this.count] || this.frames[(i - d + this.count) % this.count];
        }
      }
      if (f && this.img.src !== f.src) this.img.src = f.src;
      this.index = i;
    }

    tick() {
      cancelAnimationFrame(this.raf);
      if (!this.autoplay || this.interacted || !this.visible) return;
      var self = this;
      var last = 0;
      function step(t) {
        if (self.interacted || !self.visible) return;
        if (self.loaded >= Math.min(self.count, 24) && t - last > 1000 / AUTO_FPS) {
          last = t;
          self.show(self.index + 1);
        }
        self.raf = requestAnimationFrame(step);
      }
      this.raf = requestAnimationFrame(step);
    }

    stopAuto() {
      if (this.interacted) return;
      this.interacted = true;
      this.classList.add('is-touched');
      cancelAnimationFrame(this.raf);
    }

    bindDrag() {
      var self = this;
      var stage = this.querySelector('.w360__stage');
      var startX = 0;
      var startPos = 0;
      var dragging = false;
      // One full turn per ~1.2 widths of drag.
      function framesPerPx() { return self.count / (stage.clientWidth * 1.2); }

      function down(e) {
        if (e.button !== undefined && e.button !== 0) return;
        dragging = true;
        self.stopAuto();
        startX = e.clientX;
        startPos = self.index;
        stage.setPointerCapture && stage.setPointerCapture(e.pointerId);
        self.classList.add('is-dragging');
      }
      function move(e) {
        if (!dragging) return;
        var dx = e.clientX - startX;
        self.show(Math.round(startPos - dx * framesPerPx()));
      }
      function up() {
        dragging = false;
        self.classList.remove('is-dragging');
      }
      stage.addEventListener('pointerdown', down);
      stage.addEventListener('pointermove', move);
      stage.addEventListener('pointerup', up);
      stage.addEventListener('pointercancel', up);
      this.cleanup.push(function () {
        stage.removeEventListener('pointerdown', down);
        stage.removeEventListener('pointermove', move);
        stage.removeEventListener('pointerup', up);
        stage.removeEventListener('pointercancel', up);
      });
    }

    bindKeys() {
      var self = this;
      var stage = this.querySelector('.w360__stage');
      function key(e) {
        if (e.key === 'ArrowRight') { self.stopAuto(); self.show(self.index + 1); e.preventDefault(); }
        if (e.key === 'ArrowLeft') { self.stopAuto(); self.show(self.index - 1); e.preventDefault(); }
      }
      stage.addEventListener('keydown', key);
      this.cleanup.push(function () { stage.removeEventListener('keydown', key); });
    }
  }

  customElements.define('watch-360', Watch360);
})();
